import { useEffect, useState } from "react";
import { Gift, Copy, Users, DollarSign, Sparkles, Trophy, Check, Award, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/pages/Dashboard";

const BADGE_COLOR = {
  Bronze: "text-orange-300 border-orange-400/40 bg-orange-400/10",
  Silver: "text-slate-200 border-slate-300/40 bg-slate-300/10",
  Gold: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10",
  Platinum: "text-cyan-200 border-cyan-300/40 bg-cyan-300/10",
  Diamond: "text-fuchsia-200 border-fuchsia-300/40 bg-fuchsia-300/10",
};
const REWARD_STATUS = {
  granted: "bg-emerald-500/15 text-emerald-300",
  pending_approval: "bg-amber-500/15 text-amber-300",
  reversed: "bg-red-500/15 text-red-300",
};

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid={`referral-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className="w-5 h-5 text-[#D4AF37] mb-2" />
      <div className="text-2xl font-heading text-white">{value}</div>
      <div className="text-xs text-[#71717A]">{label}</div>
    </div>
  );
}

export default function Referrals() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    api.get("/referrals").then((r) => setData(r.data)).catch(() => {});
    api.get("/referrals/leaderboard").then((r) => setBoard(r.data.leaderboard)).catch(() => {});
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(data.share_url);
    } catch (e) {
      const el = document.querySelector('[data-testid="referral-link"]');
      if (el) { el.select(); try { document.execCommand("copy"); } catch (_) { /* ignore */ } }
    }
    toast.success("Referral link copied!");
  };

  if (!data) return <div className="h-64" />;

  const nm = data.next_milestone;
  const nmProgress = nm ? Math.min(100, Math.round(((nm.threshold - nm.remaining) / nm.threshold) * 100)) : 100;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="referrals-page">
      <PageHeader eyebrow="Grow with us" title="Refer & Earn"
        subtitle={`Give friends Fairview Systems and earn $${data.reward_per_referral.credit_usd} credit + ${data.reward_per_referral.ai_credits} bonus AI credits for every one who subscribes — plus milestone bonuses up to $1,000.`} />

      <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-r from-[#151310] to-[#121214] p-6">
        <div className="flex items-center gap-2 mb-3 text-white font-medium"><Gift className="w-5 h-5 text-[#D4AF37]" /> Your referral link</div>
        <div className="flex gap-2">
          <input readOnly value={data.share_url} data-testid="referral-link"
            className="flex-1 bg-[#0d0d0f] border border-white/10 rounded-full px-4 py-2.5 text-sm text-[#D4D4D8] truncate" />
          <button onClick={copy} data-testid="referral-copy-btn"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-5 flex items-center gap-2 hover:opacity-90">
            <Copy className="w-4 h-4" /> Copy
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Total referrals" value={data.total} />
        <Stat icon={Check} label="Qualified" value={data.qualified} />
        <Stat icon={DollarSign} label="Credit earned" value={`$${data.credit_balance_usd}`} />
        <Stat icon={Sparkles} label="Bonus AI credits" value={data.bonus_ai_credits} />
      </div>

      {/* Milestones & badges */}
      <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="referral-milestones">
        <div className="flex items-center gap-2 mb-4 text-white font-medium"><Award className="w-4 h-4 text-[#D4AF37]" /> Milestone rewards</div>
        {nm ? (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-[#A1A1AA] mb-1.5">
              <span>{nm.remaining} more to unlock <span className="text-white font-medium">{nm.badge}</span> (+${nm.bonus_usd})</span>
              <span>{data.qualified}/{nm.threshold}</span>
            </div>
            <div className="h-2 rounded-full bg-white/8 overflow-hidden">
              <div className="h-full fv-gold-gradient rounded-full transition-all" style={{ width: `${nmProgress}%` }} />
            </div>
          </div>
        ) : (
          <p className="text-sm text-emerald-300 mb-4">🎉 You've unlocked every milestone. Legend status!</p>
        )}
        <div className="flex flex-wrap gap-2">
          {data.badges.map((b) => (
            <div key={b.threshold} data-testid={`referral-badge-${b.badge.toLowerCase()}`}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${b.earned ? BADGE_COLOR[b.badge] : "border-white/10 text-[#52525B]"}`}>
              <Trophy className="w-3.5 h-3.5" /> {b.badge} · {b.threshold} refs · ${b.bonus_usd}
              {b.earned && <Check className="w-3.5 h-3.5" />}
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <h3 className="text-white font-medium mb-3">Your referrals</h3>
          {data.referrals.length === 0 ? (
            <p className="text-sm text-[#52525B]">No referrals yet — share your link to get started!</p>
          ) : data.referrals.map((r, i) => (
            <div key={i} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0" data-testid="referral-row">
              <span className="text-sm text-[#D4D4D8]">{r.email}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "qualified" ? "bg-emerald-500/15 text-emerald-300" : r.status === "reversed" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>{r.status}</span>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#D4AF37]" /> Leaderboard</h3>
          {board.length === 0 ? (
            <p className="text-sm text-[#52525B]">Be the first on the leaderboard!</p>
          ) : board.map((b, i) => (
            <div key={i} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
              <span className="text-sm text-[#D4D4D8]">#{i + 1} {b.name} {b.badge && <span className="text-[#D4AF37]">· {b.badge}</span>}</span>
              <span className="text-sm text-[#D4AF37]">{b.qualified}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reward history */}
      <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="referral-rewards-history">
        <h3 className="text-white font-medium mb-3">Reward history</h3>
        {(!data.rewards || data.rewards.length === 0) ? (
          <p className="text-sm text-[#52525B]">Your earned rewards will appear here.</p>
        ) : data.rewards.map((r, i) => (
          <div key={i} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0" data-testid="reward-row">
            <span className="text-sm text-[#D4D4D8] flex items-center gap-2">
              {r.kind === "milestone" ? <Award className="w-4 h-4 text-[#D4AF37]" /> : <Gift className="w-4 h-4 text-[#D4AF37]" />}
              {r.kind === "milestone" ? `${r.badge} milestone (${r.milestone} referrals)` : "Referral reward"}
              {r.status === "pending_approval" && <Clock className="w-3.5 h-3.5 text-amber-300" />}
            </span>
            <span className="flex items-center gap-2">
              <span className="text-sm text-white">${r.credit_usd}{r.ai_credits ? ` + ${r.ai_credits} AI` : ""}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${REWARD_STATUS[r.status] || "bg-white/10 text-[#A1A1AA]"}`}>{r.status.replace("_", " ")}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
